import { CreateDuplicationReportRequest, CreateSimplificationReportRequest, ReportType } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useToast } from '@/hooks/useToast';
import { AxiosApiError } from '@/types/apiError';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LIST_REPORTS_QUERY_KEY } from './useFetchListReports';

type CreateReportRequest =
  | ({ type: ReportType.DUPLICATION } & CreateDuplicationReportRequest)
  | ({ type: ReportType.SIMPLIFICATION } & CreateSimplificationReportRequest)
  | ({ type: ReportType.CUSTOM } & CreateDuplicationReportRequest);

export const useCreateReport = () => {
  const { getTenantId } = useTenants();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const tenantId = getTenantId();

  const { mutate: createReport, isPending: isCreating } = useMutation({
    mutationFn: async (request: CreateReportRequest) => {
      const { type, ...reportData } = request;

      if (type === ReportType.DUPLICATION) {
        return api.reports.createDuplicationReport(tenantId, reportData);
      } else if (type === ReportType.SIMPLIFICATION) {
        return api.reports.createSimplificationReport(tenantId, reportData);
      } else {
        return api.reports.createCustomReport(tenantId, reportData);
      }
    },
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: [LIST_REPORTS_QUERY_KEY], exact: false });
      showToast({ message: 'Report created successfully', type: 'success' });
    },
    onError: (error: AxiosApiError) => {
      showToast({ message: `Failed to create report: ${error.message}`, type: 'error' });
    },
  });

  return {
    createReport,
    isCreating,
  };
};
